import { useState, useEffect, useRef } from 'react';

interface Props {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

export default function Pagination({ page, totalPages, onChange }: Props) {
  const [inputVal, setInputVal] = useState(String(page));
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setInputVal(String(page)); }, [page]);
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  if (totalPages <= 1) return null;

  // Window of up to 3 pages centred on current page
  let start = Math.max(1, page - 1);
  const end = Math.min(totalPages, start + 2);
  if (end - start < 2) start = Math.max(1, end - 2);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  const go = (p: number) => onChange(Math.max(1, Math.min(totalPages, p)));

  const commitInput = () => {
    const n = parseInt(inputVal, 10);
    if (!isNaN(n)) go(n);
    else setInputVal(String(page));
    setEditing(false);
  };

  const btnBase = 'w-9 h-9 flex items-center justify-center rounded-full text-sm transition-colors';
  const btnNav  = `${btnBase} text-ink-500 hover:bg-cream-100 disabled:opacity-30 disabled:cursor-default`;
  const btnPage = `${btnBase} text-ink-700 hover:bg-cream-100`;
  const btnActive = `${btnBase} bg-sage-700 text-cream-50 font-semibold shadow-card-sm`;

  return (
    <div className="flex items-center gap-0.5">
      <button onClick={() => go(1)} disabled={page === 1} className={btnNav}>«</button>
      <button onClick={() => go(page - 1)} disabled={page === 1} className={btnNav}>‹</button>

      {pages.map(p =>
        p === page ? (
          editing ? (
            <input
              key={p}
              ref={inputRef}
              type="number"
              min={1}
              max={totalPages}
              value={inputVal}
              onChange={e => setInputVal(e.target.value)}
              onBlur={commitInput}
              onKeyDown={e => { if (e.key === 'Enter') commitInput(); if (e.key === 'Escape') { setInputVal(String(page)); setEditing(false); } }}
              className="w-9 h-9 text-center text-sm rounded-full border-2 border-sage-700 bg-cream-50 text-sage-700 font-semibold focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
            />
          ) : (
            <button key={p} onClick={() => setEditing(true)} className={btnActive} title="Klik for at gå til side">{p}</button>
          )
        ) : (
          <button key={p} onClick={() => go(p)} className={btnPage}>{p}</button>
        )
      )}

      <button onClick={() => go(page + 1)} disabled={page === totalPages} className={btnNav}>›</button>
      <button onClick={() => go(totalPages)} disabled={page === totalPages} className={btnNav}>»</button>
      <span className="ml-2 text-xs text-ink-300">af {totalPages} sider</span>
    </div>
  );
}
